import React, { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueries, useQueryClient, useQuery } from '@tanstack/react-query';
import { stockAPI, analyticsAPI, inventoryAPI, reorderAPI, smartActionsAPI, suppliersAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/contexts/RoleContext';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Search, RefreshCw, Lock } from 'lucide-react';
import SmartActionPopup    from '@/components/SmartActionPopup';
import ProductDetailModal  from '@/components/stock/ProductDetailModal';
import { OrderCartPanel, WhatsAppPreviewModal } from '@/components/PurchaseOrderCart';

import AllProductsTab   from '@/components/stock/AllProductsTab';
import LowStockTab      from '@/components/stock/LowStockTab';
import ExpiryTab        from '@/components/stock/ExpiryTab';
import DeadStockTab     from '@/components/stock/DeadStockTab';
import MarginLeakageTab from '@/components/stock/MarginLeakageTab';
import TopSellingTab    from '@/components/stock/TopSellingTab';

// ── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
  { id: 'all',     label: 'All Products'   },
  { id: 'dead',    label: 'Dead Stock'     },
  { id: 'low',     label: 'Low Stock'      },
  { id: 'leakage', label: 'Margin Leakage' },
  { id: 'expiry',  label: 'Expiry'         },
  { id: 'top',     label: 'Top Selling'    },
];

// ── Main component ────────────────────────────────────────────────────────────
const Stock = () => {
  const [searchParams]  = useSearchParams();
  const qc              = useQueryClient();
  const { storeProfile, user } = useAuth();
  const { canUseSmartActionsInStock } = useRole();
  const isSupplier = user?.role === 'supplier';
  const validTabs       = TABS.map(t => t.id);
  const initialTab      = validTabs.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'all';
  const [activeTab, setActiveTab]           = useState(initialTab);
  const [search, setSearch]                 = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [popupAction, setPopupAction]       = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isRefreshing, setIsRefreshing]     = useState(false);

  // Sort states lifted here so they survive tab switches
  const [allSort,     setAllSort]     = useState({ col: 'name',          dir: 'asc'  });
  const [deadSort,    setDeadSort]    = useState({ col: 'holding_cost',   dir: 'desc' });
  const [lowSort,     setLowSort]     = useState({ col: 'product_name',   dir: 'asc'  });
  const [leakageSort, setLeakageSort] = useState({ col: 'product_name',   dir: 'asc'  });
  const [topSort,     setTopSort]     = useState({ col: 'quantity_sold',  dir: 'desc' });
  const [expirySort,  setExpirySort]  = useState({ col: 'expiry_date',    dir: 'asc'  });

  // ── React Query: 8 parallel queries, each cached for 5 min ──
  const results = useQueries({
    queries: [
      { queryKey: ['stock-products'],   queryFn: () => stockAPI.getAll({ limit: 500 }).then(r => r.data?.items ?? r.data ?? []), staleTime: 60_000 },
      { queryKey: ['stock-expiry'],     queryFn: () => inventoryAPI.getExpiringProducts(30).then(r => r.data ?? []) },
      { queryKey: ['stock-dead'],       queryFn: () => analyticsAPI.getDeadStock().then(r => r.data ?? []) },
      { queryKey: ['stock-leakage'],    queryFn: () => analyticsAPI.getMarginLeakage(90).then(r => r.data ?? []) },
      { queryKey: ['stock-top'],        queryFn: () => analyticsAPI.getTopSelling(50, 9999).then(r => r.data ?? []) },
      { queryKey: ['stock-reorder'],    queryFn: () => reorderAPI.getSuggestions().then(r => r.data ?? []) },
      { queryKey: ['stock-wac'],        queryFn: () => analyticsAPI.getWac().then(r => r.data ?? {}) },
      { queryKey: ['stock-actions'],          queryFn: () => smartActionsAPI.getAll({ status: 'open' }).then(r => r.data ?? []) },
      { queryKey: ['stock-actions-addressed'], queryFn: () => smartActionsAPI.getAll({ status: 'addressed' }).then(r => r.data ?? []) },
      { queryKey: ['stock-actions-dismissed'], queryFn: () => smartActionsAPI.getAll({ status: 'dismissed' }).then(r => r.data ?? []) },
    ],
  });

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['stock-products'] }),
      qc.invalidateQueries({ queryKey: ['stock-expiry'] }),
      qc.invalidateQueries({ queryKey: ['stock-dead'] }),
      qc.invalidateQueries({ queryKey: ['stock-leakage'] }),
      qc.invalidateQueries({ queryKey: ['stock-top'] }),
      qc.invalidateQueries({ queryKey: ['stock-reorder'] }),
      qc.invalidateQueries({ queryKey: ['stock-wac'] }),
      qc.invalidateQueries({ queryKey: ['stock-actions'] }),
      qc.invalidateQueries({ queryKey: ['stock-actions-addressed'] }),
      qc.invalidateQueries({ queryKey: ['stock-actions-dismissed'] }),
    ]);
    setIsRefreshing(false);
  }, [qc]);

  const loading      = results.some(r => r.isLoading);
  const allProducts  = results[0].data ?? [];
  const expiryItems  = results[1].data ?? [];
  const deadStock    = results[2].data ?? [];
  const leakageData  = results[3].data ?? {};
  const leakageToday = leakageData.today?.items ?? [];
  const topSelling   = results[4].data ?? [];
  const _reorderRaw  = results[5].data;
  const reorderItems = Array.isArray(_reorderRaw) ? _reorderRaw : (_reorderRaw?.items ?? []);
  const wacData      = results[6].data ?? {};
  const wacMap       = wacData.wac  ?? {};
  const waspMap      = wacData.wasp ?? {};
  const openActions      = results[7].data ?? [];
  const addressedActions = results[8].data ?? [];
  const skippedActions   = results[9].data ?? [];

  // ── Purchase order cart state ──
  const [orderCart,        setOrderCart]        = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [showWhatsApp,     setShowWhatsApp]     = useState(false);
  const [lastSentAt,       setLastSentAt]        = useState(
    () => localStorage.getItem('kf2_order_last_sent_at')
  );

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => suppliersAPI.getAll().then(r => r.data ?? []),
    staleTime: 5 * 60 * 1000,
  });

  const cartCatalogIds = useMemo(() => new Set(orderCart.map(i => i.catalog_id)), [orderCart]);

  const handleAddToOrderFromStock = useCallback((p) => {
    if (cartCatalogIds.has(p.catalog_id)) return;
    const qty = Math.max(1, Math.round((p.reorder_level ?? 0) - (p.current_stock ?? 0)));
    setOrderCart(prev => [...prev, {
      action_id:    p.catalog_id,
      catalog_id:   p.catalog_id,
      product_name: p.product_name,
      pack_size:    [p.pack_size, p.unit].filter(Boolean).join(' · '),
      quantity:     qty,
    }]);
  }, [cartCatalogIds]);

  const handleCartUpdateQty = useCallback((action_id, qty) =>
    setOrderCart(prev => prev.map(it => it.action_id === action_id ? { ...it, quantity: qty } : it)), []);

  const handleCartRemove = useCallback((action_id) =>
    setOrderCart(prev => prev.filter(it => it.action_id !== action_id)), []);

  const batchTrackingEnabled = useMemo(
    () => allProducts.some(p => Array.isArray(p.batches) && p.batches.length > 0),
    [allProducts],
  );

  // ── Smart action handlers ──
  const handleAddress = useCallback(async (id, notes) => {
    try {
      await smartActionsAPI.address(id, notes);
      const action = openActions.find(a => a.id === id);
      qc.setQueryData(['stock-actions'], prev => (prev || []).filter(a => a.id !== id));
      qc.setQueryData(['smart-actions-open'], prev => (prev || []).filter(a => a.id !== id));
      if (action) {
        const now = new Date().toISOString();
        const addressed = { ...action, status: 'addressed', addressed_at: now };
        qc.setQueryData(['stock-actions-addressed'], prev => [addressed, ...(prev || [])]);
        qc.setQueryData(['smart-actions-addressed'], prev => [addressed, ...(prev || [])]);
      }
      toast.success('Action marked as addressed');
    } catch { toast.error('Could not update action'); }
  }, [qc, openActions]);

  const handleDismiss = useCallback(async (id) => {
    try {
      await smartActionsAPI.dismiss(id);
      const action = openActions.find(a => a.id === id);
      qc.setQueryData(['stock-actions'], prev => (prev || []).filter(a => a.id !== id));
      qc.setQueryData(['smart-actions-open'], prev => (prev || []).filter(a => a.id !== id));
      if (action) {
        const now = new Date().toISOString();
        const dismissed = { ...action, status: 'dismissed', dismissed_at: now };
        qc.setQueryData(['stock-actions-dismissed'], prev => [dismissed, ...(prev || [])]);
        qc.setQueryData(['smart-actions-skipped'], prev => [dismissed, ...(prev || [])]);
      }
      toast.success('Action skipped');
    } catch { toast.error('Could not skip action'); }
  }, [qc, openActions]);

  // ── Category map + unique list ──
  const categoryMap = useMemo(() => {
    const m = {};
    allProducts.forEach(p => { if (p.catalog_id && p.category) m[p.catalog_id] = p.category; });
    return m;
  }, [allProducts]);

  const categories = useMemo(() => {
    const set = new Set(allProducts.map(p => p.category).filter(Boolean));
    return [...set].sort();
  }, [allProducts]);

  // ── Filter helper ──
  const q = search.toLowerCase();
  const filtered = (arr, nameKey = 'name') => {
    let result = q ? arr.filter(i =>
      (i[nameKey] || '').toLowerCase().includes(q) ||
      (i.barcode || '').toLowerCase().includes(q)
    ) : arr;
    if (categoryFilter) {
      result = result.filter(i => {
        const cat = i.category || categoryMap[i.catalog_id];
        return cat === categoryFilter;
      });
    }
    return result;
  };

  const handleStockEdit = useCallback(async (catalogId, newStock) => {
    try {
      await stockAPI.setStock(catalogId, newStock);
      qc.setQueryData(['stock-products'], (prev = []) =>
        prev.map(p => p.catalog_id === catalogId ? { ...p, stock: newStock } : p)
      );
      toast.success('Stock updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update stock');
    }
  }, [qc]);

  const lowStock = reorderItems.filter(i => i.is_low_stock || i.is_out_of_stock);

  // Tab badge counts
  const counts = {
    dead:    deadStock.length,
    low:     lowStock.length,
    leakage: [...openActions, ...addressedActions, ...skippedActions].filter(a => a.type === 'margin_leakage').length,
    expiry:  (Array.isArray(expiryItems) ? expiryItems : []).filter(i => ['expired', 'critical'].includes(i.urgency)).length,
  };

  const renderContent = () => {
    if (loading) return <div className="py-16 text-center text-muted-foreground text-sm">Loading stock data…</div>;
    const commonSmartProps = { openActions, addressedActions, skippedActions, onOpenAction: setPopupAction };
    switch (activeTab) {
      case 'all':
        return <AllProductsTab allProducts={allProducts} wacMap={wacMap} waspMap={waspMap} filtered={filtered} allSort={allSort} setAllSort={setAllSort} batchTrackingEnabled={batchTrackingEnabled} onProductClick={setSelectedProduct} onStockEdit={handleStockEdit} />;
      case 'dead':
        return <DeadStockTab deadStock={deadStock} filtered={filtered} deadStockDays={storeProfile?.dead_stock_days ?? 30} deadSort={deadSort} setDeadSort={setDeadSort} {...commonSmartProps} />;
      case 'low': {
        const cartLocked = !canUseSmartActionsInStock();
        if (isSupplier) {
          return (
            <div className="h-full p-6 overflow-auto">
              <LowStockTab
                lowStock={lowStock} filtered={filtered}
                lowSort={lowSort} setLowSort={setLowSort}
                wacMap={wacMap} waspMap={waspMap}
                onAddToOrder={() => {}}
                cartCatalogIds={new Set()}
                locked={false}
              />
            </div>
          );
        }
        return (
          <div className="flex gap-4 h-full p-6">
            <div className="flex-1 min-w-0 overflow-auto">
              <LowStockTab
                lowStock={lowStock} filtered={filtered}
                lowSort={lowSort} setLowSort={setLowSort}
                wacMap={wacMap} waspMap={waspMap}
                onAddToOrder={handleAddToOrderFromStock}
                cartCatalogIds={cartCatalogIds}
                locked={cartLocked}
              />
            </div>
            <div className="w-72 shrink-0 relative">
              <OrderCartPanel
                cart={orderCart}
                onUpdateQty={handleCartUpdateQty}
                onRemove={handleCartRemove}
                onClearAll={() => setOrderCart([])}
                suppliers={suppliers}
                selectedSupplier={selectedSupplier}
                onSupplierChange={setSelectedSupplier}
                onSend={() => setShowWhatsApp(true)}
                lastSentAt={lastSentAt}
              />
              {cartLocked && (
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 pointer-events-none select-none" style={{ filter: 'blur(3px)', opacity: 0.6 }} aria-hidden="true">
                    <OrderCartPanel cart={[]} suppliers={[]} selectedSupplier="" onUpdateQty={() => {}} onRemove={() => {}} onClearAll={() => {}} onSupplierChange={() => {}} onSend={() => {}} lastSentAt={null} />
                  </div>
                  <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.10)' }} />
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="bg-card border shadow-lg rounded-xl p-5 text-center w-full">
                      <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                        <Lock className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 mb-3">
                        Growth Plan
                      </span>
                      <p className="text-xs text-muted-foreground leading-relaxed">Upgrade to create purchase orders and send supplier WhatsApp orders.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }
      case 'leakage':
        return <MarginLeakageTab leakageToday={leakageToday} filtered={filtered} leakageSort={leakageSort} setLeakageSort={setLeakageSort} {...commonSmartProps} />;
      case 'expiry':
        return <ExpiryTab expiryItems={expiryItems} filtered={filtered} batchTrackingEnabled={batchTrackingEnabled} wacMap={wacMap} {...commonSmartProps} />;
      case 'top':
        return <TopSellingTab topSelling={topSelling} allProducts={allProducts} filtered={filtered} wacMap={wacMap} topSort={topSort} setTopSort={setTopSort} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Compact unified header — tabs left, controls right */}
      <div className="flex items-center gap-3 px-6 py-2.5 border-b border-border bg-card">
        {/* Tab bar */}
        <div className="flex items-center gap-1 overflow-x-auto flex-1 min-w-0">
          {TABS.map(tab => {
            const badge = counts[tab.id];
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {tab.label}
                {badge > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-2 shrink-0">
          {categories.length > 0 && (
            <Select value={categoryFilter || '__all__'} onValueChange={v => setCategoryFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-36 h-9 text-sm">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Categories</SelectItem>
                {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search products…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 w-44 h-9 text-sm"
            />
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 rounded-lg border border-border hover:bg-muted/60 text-muted-foreground disabled:opacity-50"
            title="Refresh stock data"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className={`flex-1 bg-background ${activeTab === 'low' ? 'overflow-hidden' : 'overflow-auto'}`}>
        {renderContent()}
      </div>

      {/* Purchase order WhatsApp modal */}
      {showWhatsApp && !isSupplier && (
        <WhatsAppPreviewModal
          items={orderCart}
          supplierName={selectedSupplier}
          storeName={storeProfile?.store_name || 'Store'}
          onClose={() => setShowWhatsApp(false)}
          onMarkSent={(ts) => { setLastSentAt(ts); setOrderCart([]); setSelectedSupplier(''); setShowWhatsApp(false); }}
        />
      )}

      {/* Smart Action popup */}
      <SmartActionPopup
        open={!!popupAction}
        action={popupAction}
        onClose={() => setPopupAction(null)}
        onAddress={handleAddress}
        onDismiss={handleDismiss}
      />

      {/* Product detail modal */}
      {selectedProduct && (() => {
        const cid       = selectedProduct.catalog_id;
        const reorderInfo = reorderItems.find(r => r.catalog_id === cid);
        const topInfo     = topSelling.find(t => t.catalog_id === cid);
        const deadInfo    = deadStock.find(d => d.catalog_id === cid);
        const leakageInfo = leakageToday.find(l => l.catalog_id === cid);
        return (
          <ProductDetailModal
            product={selectedProduct}
            wac={wacMap[cid]}
            wasp={waspMap[cid]}
            reorderInfo={reorderInfo}
            topInfo={topInfo}
            deadInfo={deadInfo}
            leakageInfo={leakageInfo}
            onClose={() => setSelectedProduct(null)}
          />
        );
      })()}
    </div>
  );
};

export default Stock;
